import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Send, Bell, Users as UsersIcon, Info } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { adminNotificationService, eventsService } from '../../services';
import PageContainer from '../../components/PageContainer';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { LoadingSpinner } from '../../components/ui/Loading';
import { isAdminEquivalent } from '../../utils/permissions';

const DAILY_SEND_LIMIT = 3;

const EVENT_FILTERS = [
  { value: 'not_confirmed', label: 'Não confirmaram participação' },
  { value: 'confirmed', label: 'Confirmaram participação' },
  { value: 'confirmed_with_pilot', label: 'Confirmaram — com piloto' },
  { value: 'confirmed_without_pilot', label: 'Confirmaram — sem piloto' },
];

const AdminNotifications = () => {
  const { user } = useAuth();
  const { notify } = useNotification();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('');
  const [targetMode, setTargetMode] = useState('all');
  const [users, setUsers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState('');
  const [eventFilter, setEventFilter] = useState(EVENT_FILTERS[0].value);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const loadHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const data = await adminNotificationService.getHistory();
      setHistory(data || []);
    } catch (e) {
      console.error('Erro ao carregar histórico de notificações:', e);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdminEquivalent(user?.role)) return;
    loadHistory();
    adminNotificationService.getSubscribers()
      .then((res) => setUsers(res.users || []))
      .catch(() => setUsers([]));
    eventsService.getEvents({ page: 1, limit: 10 })
      .then((res) => setEvents(res.events || []))
      .catch(() => setEvents([]));
  }, [user, loadHistory]);

  const filteredUsers = useMemo(() => {
    const term = memberSearch.trim().toLowerCase();
    if (!term) return users;
    return users.filter((u) => {
      const nameMatch = u.name?.toLowerCase().includes(term);
      const characterMatch = (u.characters || []).some((c) => c.name?.toLowerCase().includes(term));
      return nameMatch || characterMatch;
    });
  }, [users, memberSearch]);

  const sentToday = history.filter((log) => {
    const logDate = new Date(log.createdAt);
    const now = new Date();
    return logDate.toDateString() === now.toDateString();
  }).length;
  const remainingToday = Math.max(0, DAILY_SEND_LIMIT - sentToday);
  const limitReached = remainingToday === 0;

  const toggleUser = (id) => {
    setSelectedUserIds((prev) => (prev.includes(id) ? prev.filter((u) => u !== id) : [...prev, id]));
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      notify.warning('Preencha título e mensagem.', 'Campos obrigatórios');
      return;
    }
    if (targetMode === 'users' && selectedUserIds.length === 0) {
      notify.warning('Selecione ao menos um membro.', 'Nenhum destinatário');
      return;
    }
    if (targetMode === 'event' && !eventId) {
      notify.warning('Selecione um evento.', 'Nenhum evento selecionado');
      return;
    }
    if (limitReached) {
      notify.warning(`Limite de ${DAILY_SEND_LIMIT} notificações por dia atingido.`, 'Limite diário atingido');
      return;
    }

    let target = 'all';
    if (targetMode === 'users') target = selectedUserIds;
    if (targetMode === 'event') target = { type: 'event', eventId, filter: eventFilter };

    setSending(true);
    try {
      const result = await adminNotificationService.send({
        title: title.trim(),
        body: body.trim(),
        url: url.trim() || undefined,
        target,
      });
      notify.success(`Notificação enviada para ${result.recipientCount} dispositivo(s).`, '🔔 Enviado');
      setTitle('');
      setBody('');
      setUrl('');
      setSelectedUserIds([]);
      setEventId('');
      loadHistory();
    } catch (err) {
      notify.error(err.response?.data?.error || 'Erro ao enviar notificação.', 'Erro');
    } finally {
      setSending(false);
    }
  };

  if (!isAdminEquivalent(user?.role)) {
    return (
      <PageContainer title="Notificações">
        <p className="text-red-400">Acesso negado. Apenas administradores.</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Notificações"
      description="Envie push notifications para os membros do clã"
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card variant="glass">
          <form onSubmit={handleSend} className="p-6 space-y-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Send className="w-5 h-5 text-cyan-400" />
              Nova notificação
            </h2>

            <Input
              label="Título"
              variant="glass"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={255}
              placeholder="Ex: Novo evento disponível!"
              required
            />

            <div>
              <label className="block text-sm font-semibold mb-2 text-white">Mensagem</label>
              <textarea
                className="block w-full rounded-xl border-white/20 bg-white/10 backdrop-blur-xl shadow-lg focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/50 text-white placeholder:text-gray-300 sm:text-sm px-4 py-3"
                rows={3}
                maxLength={500}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Ex: O evento de sábado já está aberto para participação."
                required
              />
            </div>

            <Input
              label="Link (opcional)"
              variant="glass"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://revoidhas.com.br/app/events/..."
            />

            <div>
              <label className="block text-sm font-semibold mb-2 text-white">Destinatários</label>
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setTargetMode('all')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    targetMode === 'all' ? 'bg-cyan-500 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  Todos os inscritos
                </button>
                <button
                  type="button"
                  onClick={() => setTargetMode('users')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    targetMode === 'users' ? 'bg-cyan-500 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  Selecionar membros
                </button>
                <button
                  type="button"
                  onClick={() => setTargetMode('event')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    targetMode === 'event' ? 'bg-cyan-500 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  Por participação em evento
                </button>
              </div>

              {targetMode === 'event' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Evento</label>
                    <select
                      value={eventId}
                      onChange={(e) => setEventId(e.target.value)}
                      className="block w-full rounded-xl border-white/20 bg-white/10 backdrop-blur-xl text-white sm:text-sm px-4 py-3"
                    >
                      <option value="" className="text-gray-900">Selecione um evento</option>
                      {events.map((ev) => (
                        <option key={ev.id} value={ev.id} className="text-gray-900">
                          {ev.name} {ev.startDate ? `(${new Date(ev.startDate).toLocaleDateString('pt-BR')})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Filtro</label>
                    <select
                      value={eventFilter}
                      onChange={(e) => setEventFilter(e.target.value)}
                      className="block w-full rounded-xl border-white/20 bg-white/10 backdrop-blur-xl text-white sm:text-sm px-4 py-3"
                    >
                      {EVENT_FILTERS.map((f) => (
                        <option key={f.value} value={f.value} className="text-gray-900">
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {targetMode === 'users' && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-400">Somente membros que já ativaram as notificações aparecem aqui.</p>
                  <Input
                    variant="glass"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    placeholder="Buscar por nome do membro ou do personagem..."
                  />
                  <div className="max-h-56 overflow-y-auto rounded-xl border border-white/10 bg-white/5 p-2 space-y-1">
                    {filteredUsers.length === 0 && (
                      <p className="text-sm text-gray-400 p-2">
                        {users.length === 0
                          ? 'Nenhum membro com notificações ativadas ainda.'
                          : 'Nenhum membro encontrado para essa busca.'}
                      </p>
                    )}
                    {filteredUsers.map((u) => (
                      <label
                        key={u.id}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/10 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(u.id)}
                          onChange={() => toggleUser(u.id)}
                          className="rounded border-white/20"
                        />
                        <div className="flex flex-col min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-white truncate">{u.name}</span>
                          </div>
                          {u.characters?.length > 0 && (
                            <span className="text-xs text-cyan-400/80 truncate">
                              {u.characters.map((c) => c.name).join(', ')}
                            </span>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-start gap-2 text-xs text-gray-400 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
              <Info className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
              <span>
                Máximo de {DAILY_SEND_LIMIT} notificações por dia — {remainingToday} restante(s) hoje.
                O histórico mostra apenas as últimas 10 notificações enviadas.
              </span>
            </div>

            <Button type="submit" disabled={sending || limitReached} fullWidth>
              {sending ? 'Enviando...' : limitReached ? 'Limite diário atingido' : 'Enviar notificação'}
            </Button>
          </form>
        </Card>

        <Card variant="glass">
          <div className="p-6">
            <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
              <Bell className="w-5 h-5 text-cyan-400" />
              Histórico
            </h2>

            {loadingHistory ? (
              <LoadingSpinner />
            ) : history.length === 0 ? (
              <p className="text-sm text-gray-400">Nenhuma notificação enviada ainda.</p>
            ) : (
              <div className="space-y-3 max-h-[28rem] overflow-y-auto">
                {history.map((log) => (
                  <div key={log.id} className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold text-white text-sm">{log.title}</p>
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {new Date(log.createdAt).toLocaleString('pt-BR')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-300 mt-1">{log.body}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                      <UsersIcon className="w-3.5 h-3.5" />
                      <span>{log.recipientCount} recebido(s)</span>
                      <span>•</span>
                      <span>{log.target === 'ALL' ? 'Todos' : 'Selecionados'}</span>
                      {log.sender?.name && (
                        <>
                          <span>•</span>
                          <span>por {log.sender.name}</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>
    </PageContainer>
  );
};

export default AdminNotifications;
